import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { channelQuotaKey, fetchOpenCharges, openChargesKey } from '../api/billing';
import { chargeTitle } from './recharge-copy';
import type { PixCharge } from './ledger';

/** Pagamento já avisado nesta aba do navegador (a barra lateral e o diálogo leem a mesma consulta). */
const announced = new Set<string>();

/**
 * Recargas em andamento da loja. Com o diálogo aberto consulta a cada 5 s;
 * fechado, a cada minuto enquanto houver pendente. Pagamento confirmado
 * atualiza a cota e avisa uma vez.
 */
export function useOpenCharges(passbookId: string | undefined, { active }: { active: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: openChargesKey(passbookId ?? 'none'),
    enabled: Boolean(passbookId),
    queryFn: () => fetchOpenCharges(passbookId!),
    retry: false,
    staleTime: 5_000,
    refetchInterval: (q) => {
      if (!q.state.data?.charges.some((charge) => charge.status === 'pending')) return false;
      return active ? 5_000 : 60_000;
    },
  });

  const charges: PixCharge[] = useMemo(() => query.data?.charges ?? [], [query.data]);

  useEffect(() => {
    const paid = charges.filter((charge) => charge.status === 'paid' && !announced.has(charge.id));
    if (!paid.length || !passbookId) return;
    paid.forEach((charge) => announced.add(charge.id));
    void queryClient.invalidateQueries({ queryKey: channelQuotaKey(passbookId) });
    toast.success(
      paid.length === 1 ? `Pagamento confirmado: ${chargeTitle(paid[0])} no seu saldo.` : 'Pagamentos confirmados. Seu saldo foi atualizado.',
    );
  }, [charges, passbookId, queryClient]);

  return {
    ...query,
    charges,
    pending: charges.filter((charge) => charge.status === 'pending'),
    prices: query.data?.prices ?? { email: 0, sms: 0 },
  };
}
